import React, { useEffect, useState } from 'react';
import Panel from '../Panel';
import { BlueBorderButton, BlueButton } from 'components/Button';
import Text from '../Text';
import {
  SubPanel,
  SubPanelRow,
  PanelRowDescriptor,
  PanelRowDisplay,
  PanelRowTextForm,
  FullPanel,
  PanelContent,
  PanelButtonRow,
  StyledForm,
  MultipleInputRowContainer,
  InputAndLabelContainer,
  LabledInput,
  InputUnitLabel
} from 'components/PanelComponents';
import SelectableSearchInput from 'components/SelectableSearchInput';
import {
  LOCAL_RESOURCE_MAPPED_VALUES,
  RESOURCE_TYPES
} from 'utils/appConstants';
import { get } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { revertSidePanel, closeSidePanel } from 'reducers/sidePanel/actions';
import { editTopoNode, deleteTopoNode } from 'reducers/toponode/actions';
import { editIndeNode, deleteIndeNode } from 'reducers/indenode/actions';
import DeleteModal from 'components/Modals/DeleteModal';
import { convertTohhmmss, convertToSeconds } from 'utils/format';

const NO_PARENT_OPTION_VALUE = 'None';
const PRIMACY_MAX_VALUE = parseInt(window.env.REACT_APP_PRIMACY_MAX_VALUE);
const PRIMACY_MIN_VALUE = parseInt(window.env.REACT_APP_PRIMACY_MIN_VALUE);

const EditNode = ({ isActive }) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const entity = useSelector(state => state.sidePanel.entity);
  const id = useSelector(state => state.sidePanel.id);
  const topoNodes = useSelector(state => state.topoNode.topoNodes);
  const indeNodes = useSelector(state => state.indeNode.indeNodes);
  const [description, setDescription] = useState('');
  const [primacy, setPrimacy] = useState('');
  const [parentTopoNodeId, setParentTopoNodeId] = useState('');
  const [node, setNode] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [descriptionIsError, setDescriptionIsError] = useState(false);
  const [newParentTopoNodeId, setNewParentTopoNodeId] = useState(null);
  const [primaryIsError, setPrimaryIsError] = useState(false);
  const [derProgramListPollRateIsError, setDerProgramListPollRateIsError] =
    useState(false);
  const [derProgramListPollRateHours, setDerProgramListPollRateHours] =
    useState(null);
  const [derProgramListPollRateMinutes, setDerProgramListPollRateMinutes] =
    useState(null);
  const [derProgramListPollRateSeconds, setDerProgramListPollRateSeconds] =
    useState(null);

  useEffect(() => {
    let node = {};
    if (entity === RESOURCE_TYPES.TOPONODE) {
      node = topoNodes.find(topoNode => topoNode.topoNodeId === id);
    } else {
      node = indeNodes.find(indeNode => indeNode.indeNodeId === id);
    }
    const convertedDerProgramListPollRate = Number.isInteger(node.pollRate)
      ? convertTohhmmss(node.pollRate)
      : {};
    setDescription(node.description || '');
    setPrimacy(node.primacy);
    setDerProgramListPollRateHours(convertedDerProgramListPollRate.hours);
    setDerProgramListPollRateMinutes(convertedDerProgramListPollRate.minutes);
    setDerProgramListPollRateSeconds(convertedDerProgramListPollRate.seconds);
    setParentTopoNodeId(node.parentTopoNodeId);
    setNewParentTopoNodeId(node.parentTopoNodeId);
    setIsLoading(false);
    setNode(node);
  }, []);

  const handleInputChange = event => {
    const target = event.target;
    const value = target.value === NO_PARENT_OPTION_VALUE ? null : target.value;
    const name = target.name;

    if (name === 'description') {
      setDescription(value);
    } else if (name === 'primacy') {
      setPrimacy(value);
    } else if (name === 'derProgramListPollRateHours') {
      setDerProgramListPollRateHours(value);
    } else if (name === 'derProgramListPollRateMinutes') {
      setDerProgramListPollRateMinutes(value);
    } else if (name === 'derProgramListPollRateSeconds') {
      setDerProgramListPollRateSeconds(value);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    let currentlyEditingNodeInfo = {};
    const isTopoNode = entity === RESOURCE_TYPES.TOPONODE;
    if (isTopoNode) {
      currentlyEditingNodeInfo = topoNodes.find(
        topoNode => topoNode.topoNodeId === id
      );
    } else {
      currentlyEditingNodeInfo = indeNodes.find(
        indeNode => indeNode.indeNodeId === id
      );
    }

    let passed = true;
    const requestBody = {};
    const parsedAsIntDerProgramListPollRateHours = parseInt(
      derProgramListPollRateHours
    );
    const parsedAsIntDerProgramListPollRateMinutes = parseInt(
      derProgramListPollRateMinutes
    );
    const parsedAsIntDerProgramListPollRateSeconds = parseInt(
      derProgramListPollRateSeconds
    );

    const convertedDerProgramListPollRate = convertToSeconds({
      hours: parsedAsIntDerProgramListPollRateHours,
      minutes: parsedAsIntDerProgramListPollRateMinutes,
      seconds: parsedAsIntDerProgramListPollRateSeconds
    });

    if (isTopoNode) {
      requestBody.topoNodeId = currentlyEditingNodeInfo.topoNodeId;

      if (newParentTopoNodeId) {
        requestBody.parentTopoNodeId = newParentTopoNodeId;
      }

      //Optional description
      if (description.length > 32) {
        setDescriptionIsError(true);
        passed = false;
      } else {
        if (description) {
          requestBody.description = description;
        }
        setDescriptionIsError(false);
      }

      //Optional pollRate
      if (
        Number.isInteger(parsedAsIntDerProgramListPollRateHours) ||
        Number.isInteger(parsedAsIntDerProgramListPollRateMinutes) ||
        Number.isInteger(parsedAsIntDerProgramListPollRateSeconds)
      ) {
        if (
          isNaN(convertedDerProgramListPollRate) ||
          convertedDerProgramListPollRate < 0 ||
          convertedDerProgramListPollRate > 4294967295
        ) {
          setDerProgramListPollRateIsError(true);
          passed = false;
        } else {
          requestBody.pollRate = convertedDerProgramListPollRate;
        }
      } else {
        setDerProgramListPollRateIsError(false);
      }

      if (!passed) {
        return;
      }

      await dispatch(editTopoNode(requestBody));
    } else {
      const parsedAsIntPrimacy = parseInt(primacy);

      if (
        !/^\d+$/.test(primacy) ||
        isNaN(parsedAsIntPrimacy) ||
        parsedAsIntPrimacy > PRIMACY_MAX_VALUE ||
        parsedAsIntPrimacy < PRIMACY_MIN_VALUE
      ) {
        setPrimaryIsError(true);
        passed = false;
      } else {
        requestBody.primacy = parsedAsIntPrimacy;
        setPrimaryIsError(false);
      }

      //Optional description
      if (description.length > 32) {
        setDescriptionIsError(true);
        passed = false;
      } else {
        if (description) {
          requestBody.description = description;
        }
        setDescriptionIsError(false);
      }

      //Optional pollRate
      if (
        Number.isInteger(parsedAsIntDerProgramListPollRateHours) ||
        Number.isInteger(parsedAsIntDerProgramListPollRateMinutes) ||
        Number.isInteger(parsedAsIntDerProgramListPollRateSeconds)
      ) {
        if (
          isNaN(convertedDerProgramListPollRate) ||
          convertedDerProgramListPollRate < 0 ||
          convertedDerProgramListPollRate > 4294967295
        ) {
          setDerProgramListPollRateIsError(true);
          passed = false;
        } else {
          requestBody.pollRate = convertedDerProgramListPollRate;
        }
      } else {
        setDerProgramListPollRateIsError(false);
      }

      if (!passed) {
        return;
      }
      await dispatch(
        editIndeNode(currentlyEditingNodeInfo.indeNodeId, requestBody)
      );
    }

    dispatch(revertSidePanel());
  };

  const handleSearchInputChange = (selectedOptions, input) => {
    if (Array.isArray(selectedOptions)) {
      if (input === 'newParentTopoNodeId') {
        setNewParentTopoNodeId(selectedOptions.map(option => option.value));
      }
      return;
    }
    if (input === 'newParentTopoNodeId') {
      setNewParentTopoNodeId(get(selectedOptions, 'value', ''));
    }
  };
  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const handleDelete = async () => {
    try {
      setShowDeleteModal(false);

      if (entity === RESOURCE_TYPES.TOPONODE) {
        await dispatch(deleteTopoNode(id));
        dispatch(closeSidePanel());
      } else {
        await dispatch(deleteIndeNode(id));
        dispatch(closeSidePanel());
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isActive || (isActive && isLoading)) {
    return null;
  } else {
    const isTopoNode = entity === RESOURCE_TYPES.TOPONODE;
    const parentNodeOptions = topoNodes
      .filter(node => {
        return (
          node.topoNodeId !== node.parentTopoNodeId &&
          node.topoNodeId !== id &&
          node.localNode !== LOCAL_RESOURCE_MAPPED_VALUES.REMOTE
        );
      })
      .map(node => node.topoNodeId);
    return (
      <>
        <StyledForm onSubmit={handleSubmit}>
          <FullPanel>
            <Panel title="Selected Node Info » Edit" primaryHeader />
            <PanelContent>
              <SubPanel>
                <SubPanelRow>
                  <PanelRowDescriptor>
                    <Text fontSize="12px">ID</Text>
                  </PanelRowDescriptor>
                  <PanelRowDisplay>
                    <Text fontSize="12px">{id}</Text>
                  </PanelRowDisplay>
                </SubPanelRow>
                <SubPanelRow>
                  <PanelRowDescriptor>
                    <Text fontSize="12px">Description</Text>
                  </PanelRowDescriptor>
                  <PanelRowDisplay>
                    <PanelRowTextForm
                      name="description"
                      onChange={handleInputChange}
                      value={description}
                      type="text"
                      minLength="1"
                      maxLength="32"
                      isError={descriptionIsError}
                    />
                  </PanelRowDisplay>
                </SubPanelRow>
                {!isTopoNode && (
                  <SubPanelRow>
                    <PanelRowDescriptor>
                      <Text fontSize="12px">Primacy</Text>
                    </PanelRowDescriptor>
                    <PanelRowTextForm
                      name="primacy"
                      onChange={handleInputChange}
                      type="number"
                      min={PRIMACY_MIN_VALUE}
                      max={PRIMACY_MAX_VALUE}
                      required
                      value={primacy}
                      isError={primaryIsError}
                    />
                  </SubPanelRow>
                )}
                <SubPanelRow>
                  <PanelRowDescriptor>
                    <Text fontSize="12px">New DER Program List Poll Rate</Text>
                    <Text fontSize="12px">(Optional)</Text>
                  </PanelRowDescriptor>

                  {/* hours */}
                  <MultipleInputRowContainer>
                    <InputAndLabelContainer width="30%">
                      <LabledInput
                        type="number"
                        name="derProgramListPollRateHours"
                        value={derProgramListPollRateHours}
                        onChange={handleInputChange}
                        min="0"
                        max="11929"
                        isError={derProgramListPollRateIsError}
                      />
                      <InputUnitLabel>hrs</InputUnitLabel>
                    </InputAndLabelContainer>
                    {/* minutes */}
                    <InputAndLabelContainer width="30%">
                      <LabledInput
                        type="number"
                        name="derProgramListPollRateMinutes"
                        value={derProgramListPollRateMinutes}
                        onChange={handleInputChange}
                        min="0"
                        max="60"
                        isError={derProgramListPollRateIsError}
                      />
                      <InputUnitLabel>min</InputUnitLabel>
                    </InputAndLabelContainer>
                    {/* seconds */}
                    <InputAndLabelContainer width="30%">
                      <LabledInput
                        type="number"
                        name="derProgramListPollRateSeconds"
                        value={derProgramListPollRateSeconds}
                        onChange={handleInputChange}
                        min="0"
                        max="60"
                        isError={derProgramListPollRateIsError}
                      />
                      <InputUnitLabel>sec</InputUnitLabel>
                    </InputAndLabelContainer>
                  </MultipleInputRowContainer>
                </SubPanelRow>
                <SubPanelRow>
                  <PanelRowDescriptor>
                    <Text fontSize="12px">Delete This Node</Text>
                  </PanelRowDescriptor>
                  <BlueBorderButton
                    type="button"
                    onClick={() => toggleDeleteModal()}
                  >
                    Delete Node
                  </BlueBorderButton>
                </SubPanelRow>
              </SubPanel>

              {isTopoNode && (
                <SubPanel>
                  <SubPanelRow>
                    <PanelRowDescriptor>
                      <Text fontSize="12px">Current Parent Node</Text>
                    </PanelRowDescriptor>
                    <PanelRowDisplay>
                      <Text fontSize="12px">{parentTopoNodeId || ''}</Text>
                    </PanelRowDisplay>
                  </SubPanelRow>
                  <SubPanelRow>
                    <PanelRowDescriptor>
                      <Text fontSize="12px">New Parent Node (Optional)</Text>
                    </PanelRowDescriptor>
                    <PanelRowDisplay>
                      <SelectableSearchInput
                        isMulti={false}
                        options={parentNodeOptions}
                        onChange={selected =>
                          handleSearchInputChange(
                            selected,
                            'newParentTopoNodeId'
                          )
                        }
                        selected={parentTopoNodeId}
                        name="parent-toponode-select"
                      />
                    </PanelRowDisplay>
                  </SubPanelRow>
                </SubPanel>
              )}
            </PanelContent>
            <PanelButtonRow>
              <BlueBorderButton
                type="button"
                onClick={() => dispatch(revertSidePanel())}
              >
                Cancel
              </BlueBorderButton>
              <BlueButton type="submit" disabled={isSubmitting}>
                Save Changes
              </BlueButton>
            </PanelButtonRow>
          </FullPanel>
        </StyledForm>
        {showDeleteModal ? (
          <DeleteModal
            toggleDeleteModal={toggleDeleteModal}
            handleDelete={handleDelete}
          />
        ) : null}
      </>
    );
  }
};

export default EditNode;
